import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { ModifyDocument } from 'src/graphql/documents';
import { importAlertState } from 'src/redux/actions/alert';
import { setUpdateDocuments } from 'src/redux/actions/documentostributarios';

const PaymentStatusBadge = ({
  meta,
  documentType,
  companyId,
  documentId,
  onUpdated,
}) => {
  const { t } = useTranslation('documents');
  const dispatch = useDispatch();
  const { executeMutation, fetching } = ModifyDocument();
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef(null);

  const isPendingPayment = !meta?.isPaid && meta?.isValid !== false;
  const isUncollectible = !!meta?.isUncollectible;

  const canMarkUncollectible = isPendingPayment && !isUncollectible;
  const canRevertUncollectible = isUncollectible;
  const isInteractive = canMarkUncollectible || canRevertUncollectible;

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const getBadgeConfig = () => {
    if (meta?.isValid === false) {
      const isReclaimed =
        documentType === 'TAX_DOCUMENT' && meta?.summary !== 'Boleta de Honorarios';
      return {
        label: isReclaimed ? t('detail.status.claimed') : t('detail.status.voided'),
        className: 'invalid',
        style: {
          background: 'linear-gradient(135deg, #fef3f3 0%, #fde8e8 100%)',
          border: '1px solid #f5c6cb',
          color: '#991b1b',
        },
      };
    }
    if (isUncollectible) {
      return { label: t('detail.status.uncollectible'), className: 'uncollectible', style: {} };
    }
    if (meta?.isPaid) {
      return { label: t('detail.status.paid'), className: 'paid', style: {} };
    }
    return { label: t('detail.status.pendingPayment'), className: 'pending', style: {} };
  };

  const { label, className, style } = getBadgeConfig();

  const updateUncollectible = async (value) => {
    if (!companyId || !documentId || fetching) return;

    try {
      const { data, error } = await executeMutation({
        companyId,
        input: [
          {
            id: documentId,
            fields: [{ key: 'meta.isUncollectible', value }],
          },
        ],
      });

      setMenuOpen(false);

      if (error || data?.modifyDocument?.nModified === 0) {
        dispatch(
          importAlertState({
            content: t('detail.updateStatusError'),
            state: 'error',
            duration: 3,
          }),
        );
        return;
      }

      dispatch(setUpdateDocuments({ value: true }));
      onUpdated?.();

      dispatch(
        importAlertState({
          content: value
            ? t('detail.markedUncollectible')
            : t('detail.markedPendingPayment'),
          state: 'success',
          duration: 3,
        }),
      );
    } catch {
      setMenuOpen(false);
      dispatch(
        importAlertState({
          content: t('detail.updateStatusError'),
          state: 'error',
          duration: 3,
        }),
      );
    }
  };

  const handleBadgeClick = () => {
    if (!isInteractive || fetching) return;
    setMenuOpen((prev) => !prev);
  };

  return (
    <div className="status-badge-wrapper" ref={containerRef}>
      <span
        className={`status-badge ${className}${isInteractive ? ' status-badge--interactive' : ''}`}
        style={style}
        onClick={handleBadgeClick}
        onKeyDown={(e) => {
          if (isInteractive && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleBadgeClick();
          }
        }}
        role={isInteractive ? 'button' : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        aria-haspopup={isInteractive ? 'menu' : undefined}
        aria-expanded={isInteractive ? menuOpen : undefined}
      >
        {label}
        {isInteractive && <span className="status-badge-chevron">▾</span>}
      </span>

      {menuOpen && isInteractive && (
        <ul className="status-badge-menu" role="menu">
          {canMarkUncollectible && (
            <li role="none">
              <button
                type="button"
                role="menuitem"
                className="status-badge-menu-item"
                disabled={fetching}
                onClick={() => updateUncollectible(true)}
              >
                {t('detail.status.uncollectible')}
              </button>
            </li>
          )}
          {canRevertUncollectible && (
            <li role="none">
              <button
                type="button"
                role="menuitem"
                className="status-badge-menu-item"
                disabled={fetching}
                onClick={() => updateUncollectible(false)}
              >
                {t('detail.status.pendingPayment')}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default PaymentStatusBadge;
